import { Controller } from "@hotwired/stimulus"

// The Sign up page's form (TMS-1643). Sign up stays disabled until the terms are agreed. Submitting
// checks every field and shows what is wrong under it, focusing the first; fixing a field clears
// its error as they type. Leaving the password or Repeat Password field checks it straight away.
// A field's hint (the password rule) gives way to its error and comes back once it is fixed.
// The server checks all of this again (Users::RegisterWithImisService).
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const PASSWORD_POLICY = /^(?=.*[A-Za-z])(?=.*\d).{7,}$/

const MESSAGES = {
  name: "Please enter your first and last name.",
  email: "Please enter a valid email address.",
  password: "Your password needs at least 7 characters, including a letter and a number.",
  confirmation: "Your passwords don’t match.",
}

export default class extends Controller {
  static targets = ["name", "email", "password", "confirmation", "terms", "submit"]

  connect() {
    this.sync()
    this.describeHints()
  }

  // The password rule is read out with the field, not only seen under it.
  describeHints() {
    this.fields().forEach((field) => {
      const input = this[`${field}Target`]
      const hint = this.group(input).querySelector(".app-input-hint")
      if (!hint) return

      hint.id ||= `${input.id}_hint`
      input.setAttribute("aria-describedby", hint.id)
    })
  }

  sync() {
    this.submitTarget.disabled = !this.termsTarget.checked
  }

  validate(event) {
    const invalid = this.fields().filter((field) => this.check(field))
    if (invalid.length === 0) return

    event.preventDefault()
    this[`${invalid[0]}Target`].focus()
  }

  // Only a field already showing an error is rechecked while typing, so nobody is told off mid-word.
  revalidate(event) {
    const field = this.fields().find((name) => this[`${name}Target`] === event.target)
    if (field && this.hasError(event.target)) this.check(field)
    if (field === "password" && this.hasError(this.confirmationTarget)) this.check("confirmation")
  }

  checkPassword() {
    if (this.passwordTarget.value) this.check("password")
  }

  checkConfirmation() {
    if (this.confirmationTarget.value) this.check("confirmation")
  }

  fields() {
    return ["name", "email", "password", "confirmation"]
  }

  // Shows or clears the field's error; true when it has one.
  check(field) {
    const input = this[`${field}Target`]
    const invalid = !this.valid(field, input.value)

    invalid ? this.showError(input, MESSAGES[field]) : this.clearError(input)
    return invalid
  }

  valid(field, value) {
    switch (field) {
      case "name": return value.trim().split(/\s+/).length > 1
      case "email": return EMAIL_PATTERN.test(value.trim())
      case "password": return PASSWORD_POLICY.test(value)
      case "confirmation": return value.length > 0 && value === this.passwordTarget.value
    }
  }

  showError(input, message) {
    const group = this.group(input)
    let hint = group.querySelector(".app-input-hint")

    if (!hint) {
      hint = document.createElement("div")
      hint.className = "app-input-hint"
      group.appendChild(hint)
    }
    hint.id ||= `${input.id}_error`
    hint.textContent = message
    hint.classList.add("is-error")
    hint.setAttribute("role", "alert")
    group.classList.add("has-error")
    input.setAttribute("aria-invalid", "true")
    input.setAttribute("aria-describedby", hint.id)
  }

  clearError(input) {
    const group = this.group(input)
    const hint = group.querySelector(".app-input-hint")

    group.classList.remove("has-error")
    input.removeAttribute("aria-invalid")
    if (!hint) return

    if (input.dataset.hint) {
      hint.textContent = input.dataset.hint
      hint.classList.remove("is-error")
      hint.removeAttribute("role")
    } else {
      hint.remove()
      input.removeAttribute("aria-describedby")
    }
  }

  hasError(input) {
    return this.group(input).classList.contains("has-error")
  }

  group(input) {
    return input.closest(".app-input-group")
  }
}
